/**
 * Схема зала на кассе.
 *
 * Это план помещения, а не диалог: ей отдан весь экран. Пол в клетку,
 * стены по периметру, столы стоят на своих местах, диваны вдоль стен,
 * сцена и барная стойка подписаны — официант узнаёт СВОЙ зал,
 * а не абстрактную сетку.
 *
 * Залы — колонкой слева, как двери в коридоре: официант работает
 * в одном зале, а не во всех сразу. На каждой «двери» видно, что за ней:
 * сколько столов занято и не зовут ли там — тревога из соседнего зала
 * не должна ждать, пока в него заглянут.
 *
 * Три вопроса, на каждый отвечает один взгляд:
 *   куда посадить пришедших — свободные столы тихие, с числом мест;
 *   где ждут счёт — терракотовая плашка «счёт» и слово «ждём оплату»;
 *   кто зовёт — единственное состояние с гало, плашка «зовут».
 *
 * Плюс на занятом столе: сумма и сколько минут сидят. Чужой стол
 * подписан именем официанта — понятно, к кому обратиться.
 *
 * Столов бывает 6, а бывает 40: масштаб клетки считается под ширину
 * И высоту экрана — зал всегда виден целиком, без прокрутки посреди
 * смены. В плотном зале мелкие подписи уходят, номер и тревоги остаются.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { HallLive, HallPlanList, LiveTable, PlanShape } from '@dastarhan/contracts';
import { BASE } from '../api';

const STATE_CLASS: Record<LiveTable['state'], string> = {
  FREE: 'live-free',
  BUSY: 'live-busy',
  BILL: 'live-bill',
  CALL: 'live-call',
  RESERVED: 'live-reserved',
};

/** Подписи мебели по умолчанию; владелец может переназвать в редакторе. */
const FURNITURE_LABEL: Partial<Record<PlanShape['kind'], string>> = {
  STAGE: 'Сцена',
  BAR: 'Барная стойка',
  ENTRANCE: 'Вход',
  WC: 'WC',
};

/** Виды, где цвет решает дизайн-система, а не сохранённый hex. */
const SEMANTIC = new Set(['SOFA', 'STAGE', 'BAR', 'ENTRANCE', 'WC', 'WINDOW', 'PLANT']);

const money = (v: number) => `${Math.round(v / 100).toLocaleString('ru-RU')}₸`;

export function HallShapeView({ s, cell }: { s: PlanShape; cell: number }) {
  const semantic = SEMANTIC.has(s.kind);
  const label = s.kind === 'TEXT' ? s.text : (s.text ?? FURNITURE_LABEL[s.kind] ?? null);
  return (
    <div
      className={`hall-shape ${s.kind.toLowerCase()}`}
      style={{
        left: s.x * cell, top: s.y * cell,
        width: s.w * cell, height: s.h * cell,
        // сырая геометрия хранит свой цвет; мебель красит система
        ...(semantic ? {} : s.kind === 'TEXT'
          ? { color: `#${s.color}` }
          : { background: `#${s.color}` }),
        transform: s.rotate ? `rotate(${s.rotate}deg)` : undefined,
      }}
    >{semantic || s.kind === 'TEXT' ? label : null}</div>
  );
}

export function HallMap({
  token, onPick, onClose, senior = false, canPay = false,
}: {
  token: string;
  onPick: (tableId: string, tableName: string) => void;
  /** старший входит в любой стол; официант — только в свой */
  senior?: boolean;
  /** право брать деньги: стол со счётом открывает только кассир */
  canPay?: boolean;
  onClose: () => void;
}) {
  const headers = { Authorization: `Bearer ${token}` };

  const plan = useQuery({
    queryKey: ['hall-plan-pos'],
    queryFn: async (): Promise<HallPlanList> =>
      (await fetch(`${BASE}/hall-plan`, { headers })).json(),
    staleTime: 5 * 60_000,
  });

  const live = useQuery({
    queryKey: ['hall-live'],
    queryFn: async (): Promise<HallLive> =>
      (await fetch(`${BASE}/hall-plan/live`, { headers })).json(),
    refetchInterval: 10_000,
  });

  // «Только мои» — не запрет, а помощь: в полном зале официант ищет
  // свои столы глазами по всей схеме, и это отнимает секунды каждый раз
  const [onlyMine, setOnlyMine] = useState(false);
  // Выбранный зал переживает закрытие схемы: официант весь вечер
  // работает в одном и том же
  const [hallPick, setHallPick] = useState<string | null>(
    () => localStorage.getItem('dastarhan.hallId'),
  );

  const halls = plan.data?.halls ?? [];
  const liveTables = live.data?.tables ?? [];
  const stateBy = new Map(liveTables.map((t) => [t.id, t]));
  const placedAll = halls.flatMap((h) => h.tables.filter((t) => t.x !== null));

  const calls = liveTables.filter((t) => t.state === 'CALL' || t.state === 'BILL');
  const myCount = liveTables.filter((t) => t.mine).length;

  // Залы, у которых есть нарисованный план; счётчики — на «двери» зала
  const drawn = halls
    .map((h) => {
      const tables = h.tables.filter((t) => t.x !== null);
      const states = tables.map((t) => stateBy.get(t.id)?.state ?? 'FREE');
      return {
        hall: h,
        tables,
        busy: states.filter((s) => s !== 'FREE' && s !== 'RESERVED').length,
        alarms: states.filter((s) => s === 'CALL' || s === 'BILL').length,
      };
    })
    .filter((x) => x.tables.length > 0);

  const current = drawn.find((x) => x.hall.hallId === hallPick) ?? drawn[0];
  const pickHall = (id: string) => {
    setHallPick(id);
    localStorage.setItem('dastarhan.hallId', id);
  };

  const freeCount = current
    ? current.tables.filter((t) => (stateBy.get(t.id)?.state ?? 'FREE') === 'FREE').length
    : 0;

  // Схема не нарисована — не выдумываем сетку, честно говорим об этом
  if (plan.isSuccess && placedAll.length === 0) {
    return (
      <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Схема зала">
        <div className="dialog" style={{ maxWidth: 460, width: '100%' }}>
          <span className="eyebrow">Схема зала</span>
          <h1>Схема ещё не нарисована</h1>
          <p className="hint">
            Откройте офис → «Зал» → «Схема зала» и расставьте столы мышкой.
            Пока схемы нет, пользуйтесь списком столов.
          </p>
          <div className="dialog-actions">
            <button className="btn ghost" onClick={onClose}>Закрыть</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hall-screen" role="dialog" aria-modal="true" aria-label="Схема зала">
      <header className="hall-head">
        <h1>Схема зала</h1>
        {calls.length > 0 && (
          <span className="hall-alarm">Ждут внимания: {calls.length}</span>
        )}
        {current && (
          <span className="hall-count">Свободно: {freeCount} из {current.tables.length}</span>
        )}
        {myCount > 0 && (
          <button className={`chip ${onlyMine ? 'on' : ''}`}
            onClick={() => setOnlyMine((v) => !v)}>
            Мои столы · {myCount}
          </button>
        )}
        <span className="spacer" />
        <button className="btn ghost" onClick={onClose}>Закрыть · Esc</button>
      </header>

      <div className="hall-body">
        {/* Список залов — колонкой слева, как у входа в каждый зал.
            Счётчик занятости и тревога видны, не заходя внутрь */}
        {drawn.length > 1 && (
          <nav className="hall-rail" aria-label="Залы">
            {drawn.map(({ hall, tables, busy, alarms }) => (
              <button key={hall.hallId}
                className={current?.hall.hallId === hall.hallId ? 'on' : ''}
                aria-current={current?.hall.hallId === hall.hallId}
                onClick={() => pickHall(hall.hallId)}>
                <b>{hall.hallName}</b>
                <span className="hr-meta">
                  {busy > 0 ? `занято ${busy} из ${tables.length}` : `свободен · ${tables.length} ст.`}
                  {alarms > 0 && <i className="hr-alarm">{alarms}</i>}
                </span>
              </button>
            ))}
          </nav>
        )}

        <div className="hall-scroll">
          {plan.isPending && <p className="hint">Загружаем схему…</p>}
          {plan.isError && (
            <p className="hint">Схема недоступна без связи — пользуйтесь списком столов.</p>
          )}
          {current && (() => {
            const { hall: h, tables } = current;
            const cols = Math.max(
              ...tables.map((t) => t.x! + t.w),
              ...h.shapes.map((s) => s.x + s.w),
              1,
            ) + 1;
            const rows = Math.max(
              ...tables.map((t) => t.y! + t.h),
              ...h.shapes.map((s) => s.y + s.h),
              1,
            ) + 1;
            // Масштаб под зал по обеим осям: маленький дышит, большой
            // умещается целиком — прокрутка посреди смены отнимает секунды.
            // Ниже 22px на клетку подписи-мелочи прячутся (класс dense)
            const railW = drawn.length > 1 ? 224 : 0;
            const availW = Math.max(480, Math.min(window.innerWidth, 1560) - railW - 72);
            const availH = Math.max(360, window.innerHeight - 210);
            const cell = Math.max(14, Math.min(36,
              Math.floor(availW / cols), Math.floor(availH / rows)));
            const dense = cell < 22;

            return (
              <div className="hall-block">
                <div className={`hall-board ${dense ? 'dense' : ''}`}
                  style={{
                    width: cols * cell, height: rows * cell,
                    backgroundSize: `${cell}px ${cell}px`,
                  }}>
                  {h.shapes.map((s) => <HallShapeView key={s.id} s={s} cell={cell} />)}

                  {tables.map((t) => {
                    const st = stateBy.get(t.id);
                    const state = st?.state ?? 'FREE';
                    return (
                      <button
                        key={t.id}
                        className={`hall-table ${t.shape.toLowerCase()} ${STATE_CLASS[state]}`
                          + (st?.mine ? ' mine' : '')
                          + (onlyMine && !st?.mine && state === 'BUSY' ? ' dimmed' : '')
                          // Узкий стол (диван у стены, двухместный) кладёт
                          // подписи в строку — иначе они вылезут за контур
                          + (t.w > t.h * 2 ? ' wide' : '')}
                        style={{
                          left: t.x! * cell, top: t.y! * cell,
                          width: t.w * cell, height: t.h * cell,
                        }}
                        onClick={() => {
                          // Чужой занятый стол официанту закрыт: начать
                          // на нём заказ значит смешать своих гостей
                          // с чужими в одном счёте
                          // Счёт закрыт — стол ждёт кассира, даже если
                          // он свой: гость держит бумагу с суммой,
                          // и дописывать в неё нельзя
                          if (st?.state === 'BILL' && !canPay) {
                            alert(
                              `Счёт по столу ${t.name} закрыт.\n\n`
                              + 'Гость получил его на руки — дальше работает кассир.',
                            );
                            return;
                          }
                          const busyForeign = st && !st.mine
                            && (st.state === 'BUSY' || st.state === 'BILL');
                          if (busyForeign && !senior) {
                            alert(
                              `Стол ${t.name} ведёт ${st.waiter ?? 'другой официант'}.\n\n`
                              + 'Позовите его или старшего.',
                            );
                            return;
                          }
                          onPick(t.id, t.name);
                        }}
                      >
                        <b>{t.name}</b>
                        {state === 'BUSY' && st?.total !== null && (
                          <span className="ht-sum">{money(st!.total!)}</span>
                        )}
                        {state === 'BUSY' && st?.busyMin !== null && (
                          <span className="ht-min">{st!.busyMin} мин</span>
                        )}
                        {/* Чужой стол подписан именем: официант понимает,
                            к кому обратиться, а не гадает */}
                        {state === 'BUSY' && !st?.mine && st?.waiter && (
                          <span className="ht-waiter">{st.waiter}</span>
                        )}
                        {/* Счёт подан: стол ждёт денег, а не заказа.
                            Цвет подсказывает издалека, слово подтверждает вблизи */}
                        {st?.awaitingPay && <span className="ht-wait">ждём оплату</span>}
                        {state === 'BILL' && !st?.mine && st?.waiter && (
                          <span className="ht-waiter">{st.waiter}</span>
                        )}
                        {state === 'CALL' && <span className="ht-alarm">зовут</span>}
                        {state === 'BILL' && <span className="ht-alarm">счёт</span>}
                        {state === 'RESERVED' && (
                          <span className="ht-res">{st?.reservedAt}</span>
                        )}
                        {state === 'FREE' && <span className="ht-seats">{t.seats} мест</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="hall-legend">
        <span><i className="live-free" />свободен</span>
        <span><i className="live-busy" />занят</span>
        <span><i className="live-bill" />просят счёт</span>
        <span><i className="live-call" />зовут официанта</span>
        <span><i className="live-reserved" />бронь скоро</span>
      </div>
    </div>
  );
}
